import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import Auth from "./Auth";
import { Upload, BarChart3, Search, Plus, X, Camera, Loader2, ScanLine, Home, Clock, Settings, Globe, Download, Star, Fingerprint } from "lucide-react";
import * as XLSX from "xlsx";
import { BarChart, ComposedChart, Bar, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";

const PROXY_URL         = "https://wine-cellar-api.vercel.app/api/sheets";
const WINDOW_URL        = "https://wine-cellar-api.vercel.app/api/window";
const MOVEMENTS_URL     = "https://wine-cellar-api.vercel.app/api/movements";
const TARGET_RANGES_URL = "https://wine-cellar-api.vercel.app/api/target-ranges";
const AUTH_URL          = "https://wine-cellar-api.vercel.app/api/auth";

function bufferToBase64(buf) {
  return btoa(Array.from(new Uint8Array(buf), b => String.fromCharCode(b)).join("")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const VINTAGE_CATEGORIES = [
  { id: "burgundy_red",   label: "Burgundy Red" },
  { id: "burgundy_white", label: "Burgundy White" },
  { id: "other_red",      label: "Other Red" },
  { id: "other_white",    label: "Other White" },
];
const EMPTY_VINTAGE_STOCK_BY_CATEGORY = { burgundy_red: [], burgundy_white: [], other_red: [], other_white: [] };
const EMPTY_TARGET_RANGES = { burgundy_red: [], burgundy_white: [], other_red: [], other_white: [] };

// Legacy movement reasons were recorded in a mix of English and French.
// This normalizes any stored value to a canonical French label for display.
const REASON_FR = {
  consumed: "conso", gifted: "offert", sold: "vendu", purchase: "achat",
  "consommé": "conso", conso: "conso", offert: "offert", vendu: "vendu", achat: "achat",
};
function translateReason(reason) {
  const key = String(reason || "").toLowerCase().trim();
  return REASON_FR[key] || reason;
}

async function dbGet() {
  const r = await fetch(PROXY_URL);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const data = await r.json();
  return data.wines.map(w => ({
    id: Number(w.id),
    name: w.name || "",
    appellation: normalizeAppellation(w.appellation) || "",
    vintage: w.vintage || "",
    estate: w.estate || "",
    quantity: Number(w.quantity) || 0,
    price: Number(w.price) || 0,
    color: normalizeColor(w.color) || "Rouge",
    location: w.location || "",
    drinkFrom: w.drink_from || "",
    apogeStart: w.apoge_start || "",
    apogeEnd: w.apoge_end || "",
    drinkBy: w.drink_by || "",
    drinkWindowAI: w.drink_window_ai === true,
    grapes: w.grapes || "",
    country: w.country || "",
    description: w.description || "",
    foodPairing: w.food_pairing || "",
    rating: Number(w.rating) || 0,
  }));
}
async function dbUpsert(wine) {
  const r = await fetch(PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "upsert", wine }),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}
async function dbDelete(id) {
  const r = await fetch(PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "delete", id }),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function recordMovement(wine_id, vintage, quantity, type, reason) {
  try {
    await fetch(MOVEMENTS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        wine_id: wine_id || null,
        vintage: String(vintage || ""),
        quantity: Number(quantity),
        type,
        reason,
        date: new Date().toISOString().split("T")[0],
      }),
    });
  } catch (e) {
    console.error("Movement record failed:", e);
  }
}

function parseExcelDate(raw) {
  if (!raw) return null;
  if (typeof raw === "number") {
    const d = new Date(Math.round((raw - 25569) * 86400 * 1000));
    if (!isNaN(d)) return d.toISOString().split("T")[0];
  }
  const str = String(raw).trim();
  const dmy = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const d = new Date(str);
  return isNaN(d) ? null : d.toISOString().split("T")[0];
}

// Wines were historically stored with a mix of English and French color
// labels ("Red"/"White"). The backend now canonicalizes on write, but this
// normalizes on read too so display is correct even for not-yet-migrated data.
const COLOR_DISPLAY = { red: "Rouge", white: "Blanc" };
function normalizeColor(color) {
  return COLOR_DISPLAY[(color || "").toLowerCase().trim()] || color;
}

// Historical entries mixed "1er Cru" and "Premier Cru" (and inconsistent
// casing) for the same appellation, fragmenting them into separate rows
// wherever appellation is grouped. The backend now canonicalizes on write,
// but this normalizes on read too so display is correct immediately.
function normalizeAppellation(appellation) {
  if (!appellation) return appellation;
  return appellation
    .replace(/1er\s+cru/gi, "Premier Cru")
    .replace(/premier\s+cru/gi, "Premier Cru")
    .replace(/hautes\s+cotes\s+de\s+nuits/gi, "Hautes Côtes de Nuits")
    .replace(/hautes\s+cotes\s+de\s+beaune/gi, "Hautes Côtes de Beaune");
}

// No dedicated sub-region field on a wine — infer Côte de Nuits / Côte de
// Beaune from the appellation, which is always a commune/climat name for
// Burgundy wines. Anything else (other Burgundy sub-regions, other wine
// regions entirely) falls into "other".
const COTE_DE_NUITS_KEYWORDS = [
  "marsannay", "fixin", "gevrey", "chambertin", "morey", "chambolle", "musigny",
  "bonnes-mares", "bonnes mares", "vougeot", "échezeaux", "echezeaux", "vosne",
  "romanée", "romanee", "richebourg", "tâche", "tache", "grande rue", "nuits",
  "clos de tart", "clos de la roche", "clos saint-denis", "clos saint denis",
  "clos des lambrays", "comblanchien", "corgoloin",
];
const COTE_DE_BEAUNE_KEYWORDS = [
  "ladoix", "aloxe", "corton", "pernand", "chorey", "savigny", "beaune",
  "pommard", "volnay", "monthelie", "monthélie", "auxey", "saint-romain", "saint romain",
  "meursault", "blagny", "puligny", "chassagne", "montrachet", "saint-aubin", "saint aubin",
  "santenay", "maranges", "côte de beaune", "cote de beaune",
  "hautes côtes de beaune", "hautes cotes de beaune",
];
function classifyCoteAppellation(appellation) {
  const a = (appellation || "").toLowerCase();
  if (COTE_DE_NUITS_KEYWORDS.some(k => a.includes(k)))  return "nuits";
  if (COTE_DE_BEAUNE_KEYWORDS.some(k => a.includes(k))) return "beaune";
  return "other";
}

function wineColorHex(color) {
  const c = (color || "").toLowerCase().trim();
  if (c === "red"   || c === "rouge")                                                      return "#8B1A1A";
  if (c === "white" || c === "blanc")                                                      return "#C9A84C";
  if (c === "rose"  || c === "rosé")                                                       return "#D46070";
  if (c === "sparkling" || c === "pétillant" || c === "mousseux" || c === "effervescent") return "#7090B0";
  return "#83725A";
}

const StarRating = ({ value = 0, onChange, size = "w-5 h-5" }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n === value ? 0 : n)}
        className="p-0.5 -m-0.5"
        aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
      >
        <Star className={`${size} ${n <= value ? "fill-cgold text-cgold" : "text-cmuted"}`} />
      </button>
    ))}
  </div>
);

// ── Vintage target chart ─────────────────────────────────────────────────────
const VintageTargetChart = ({ data, onToggleGold }) => {
  if (!data || data.length === 0) return (
    <p className="text-cmuted text-sm text-center py-8 font-sans">No vintage data available</p>
  );

  const ROW_H    = 36;
  const LABEL_W  = 66;
  const DELTA_W  = 40; // fixed right-hand column for the +/- differential
  const BW = 14, BH = 26; // bottle bounding box
  const PAD      = { top: 24, right: 24 + DELTA_W, bottom: 34, left: LABEL_W };
  const VIEW_W   = 340;
  const CHART_W  = VIEW_W - PAD.left - PAD.right;
  const CHART_H  = data.length * ROW_H;
  const TOTAL_H  = CHART_H + PAD.top + PAD.bottom;

  // Headroom so the highest bar doesn't sit flush against the chart edge —
  // otherwise its count label collides with the fixed delta column.
  const maxVal      = Math.ceil(Math.max(...data.map(d => Math.max(d.current, d.targetMax)), 70) * 1.15);
  const xScale      = v => (v / maxVal) * CHART_W;
  const chartLeft   = PAD.left;
  const chartRight  = PAD.left + CHART_W;

  const TICKS   = 5;
  const ticks   = Array.from({ length: TICKS }, (_, i) => Math.round((maxVal / (TICKS - 1)) * i));

  const RED = "#722F37", GREEN = "#4A7C59", GOLD = "#C4922A";

  // Bottle SVG path centred at (cx, cy) — outline only
  const BottleShape = ({ cx, cy, color }) => {
    const x     = cx - BW / 2;
    const y     = cy - BH / 2;
    const neckW = BW * 0.32;
    const neckH = BH * 0.28;
    const nx    = x + (BW - neckW) / 2;
    const by    = y + neckH;
    const r     = 3;
    const d = [
      `M ${nx} ${y}`,
      `h ${neckW}`,
      `v ${neckH * 0.55}`,
      `c ${neckW * 0.6} ${neckH * 0.3} ${BW * 0.55} ${neckH * 0.4} ${x + BW - nx - neckW} ${neckH * 0.45}`,
      `H ${x + BW - r}`,
      `q ${r} 0 ${r} ${r}`,
      `V ${y + BH - r}`,
      `q 0 ${r} ${-r} ${r}`,
      `H ${x + r}`,
      `q ${-r} 0 ${-r} ${-r}`,
      `V ${by + r}`,
      `q 0 ${-r} ${r} ${-r}`,
      `H ${nx}`,
      `Z`,
    ].join(" ");
    return <path d={d} fill="none" stroke={color} strokeWidth={1.5} opacity={0.95} />;
  };

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${VIEW_W} ${TOTAL_H}`} style={{ width: "100%", minWidth: 300, height: "auto" }}>

        {/* Per-row too-little / in-range / too-much bands */}
        {data.map((d, i) => {
          const y       = PAD.top + i * ROW_H;
          const cy      = y + ROW_H / 2;
          const bandTop = cy - (ROW_H - 8) / 2;
          const bandH   = ROW_H - 8;
          const rx1     = chartLeft + xScale(d.targetMin);
          const rx2     = chartLeft + xScale(d.targetMax);
          return (
            <g key={`band-${d.vintage}`}>
              {rx1 > chartLeft && <rect x={chartLeft} y={bandTop} width={rx1 - chartLeft} height={bandH} fill={RED} opacity={0.16} rx={3} />}
              {rx2 > rx1       && <rect x={rx1} y={bandTop} width={rx2 - rx1} height={bandH} fill={GREEN} opacity={0.20} rx={3} />}
              {chartRight > rx2 && <rect x={rx2} y={bandTop} width={chartRight - rx2} height={bandH} fill={GOLD} opacity={0.16} rx={3} />}
            </g>
          );
        })}

        {/* Grid + x-axis labels (top and bottom) */}
        {ticks.map(t => (
          <g key={t}>
            <line
              x1={chartLeft + xScale(t)} y1={PAD.top}
              x2={chartLeft + xScale(t)} y2={PAD.top + CHART_H}
              stroke="rgba(42,32,24,0.12)" strokeWidth={1}
            />
            <text
              x={chartLeft + xScale(t)} y={14}
              textAnchor="middle" fill="rgba(42,32,24,0.55)" fontSize={13} fontWeight={600}
            >{t}</text>
            <text
              x={chartLeft + xScale(t)} y={TOTAL_H - 10}
              textAnchor="middle" fill="rgba(42,32,24,0.55)" fontSize={13} fontWeight={600}
            >{t}</text>
          </g>
        ))}

        {data.map((d, i) => {
          const y     = PAD.top + i * ROW_H;
          const cy    = y + ROW_H / 2;
          const under = d.current < d.targetMin;
          const over  = d.current > d.targetMax;
          const color = under ? RED : over ? GOLD : GREEN;
          const bx    = chartLeft + xScale(d.current);

          const delta = under && d.targetMin > 0
            ? `-${d.targetMin - d.current}`
            : over
            ? `+${d.current - d.targetMax}`
            : "";
          const deltaColor = under
            ? "rgba(176,60,60,0.95)"
            : "rgba(150,110,25,0.95)";

          return (
            <g key={d.vintage}>
              {/* Vintage label with a clickable gold-year star — dim until toggled on */}
              <g
                onClick={() => onToggleGold && onToggleGold(d.vintage)}
                style={{ cursor: onToggleGold ? "pointer" : "default" }}
              >
                <title>{d.gold ? "Marked as a gold vintage — click to unmark" : "Click to mark as a gold vintage"}</title>
                <rect x={0} y={cy - ROW_H / 2} width={PAD.left} height={ROW_H} fill="transparent" />
                <text
                  x={PAD.left - 8} y={cy + 5}
                  textAnchor="end" fontSize={15} fontWeight={700}
                >
                  <tspan fill={d.gold ? "#C4922A" : "rgba(42,32,24,0.15)"} fontSize={14}>★ </tspan>
                  <tspan fill="rgba(42,32,24,0.9)">{d.vintage}</tspan>
                </text>
              </g>

              {/* Bottle icon (outline only) */}
              {d.current > 0 && <BottleShape cx={bx} cy={cy} color={color} />}

              {/* Count label */}
              {d.current > 0 && (
                <text
                  x={bx + BW / 2 + 6} y={cy + 5}
                  fill="rgba(42,32,24,0.75)" fontSize={13} fontWeight={600}
                >{d.current}</text>
              )}

              {/* Delta label — fixed right-hand column, aligned across all rows */}
              {delta && (
                <text
                  x={chartRight + 20} y={cy + 5}
                  fill={deltaColor} fontSize={13} fontWeight={700}
                >{delta}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const LabelScanner = ({ onClose, onAddWine }) => {
  const [image, setImage] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(URL.createObjectURL(file));
    setResult(null); setError(null);
    const img = new Image();
    img.onload = () => {
      const MAX = 1024;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      setImageBase64(dataUrl.split(",")[1]);
    };
    img.src = URL.createObjectURL(file);
  };

  const scanLabel = async () => {
    if (!imageBase64) return;
    setScanning(true); setError(null);
    try {
      const r = await fetch("https://wine-cellar-api.vercel.app/api/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64, mediaType: "image/jpeg" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const res = await r.json();
      if (res.error) throw new Error(res.error);
      setResult(res);
    } catch (e) { setError(e.message || "Could not read the label."); }
    finally { setScanning(false); }
  };

  const inp = "w-full px-3 py-2.5 bg-cbg text-cpaper rounded-xl border border-cborder focus:border-cgold focus:outline-none text-sm font-sans";

  return (
    <div className="fixed inset-0 bg-black/85 flex items-end sm:items-center justify-center z-50">
      <div className="bg-csurf rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-lg border border-cborder max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-cgold" />
            <h2 className="text-lg font-semibold text-cpaper font-sans">Scan Label</h2>
          </div>
          <button onClick={onClose} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
        </div>
        <div
          onClick={() => fileRef.current.click()}
          className="border-2 border-dashed border-cborder rounded-xl p-8 mb-4 cursor-pointer hover:border-cgold transition flex flex-col items-center gap-3"
        >
          {image
            ? <img src={image} alt="label" className="max-h-48 rounded-lg object-contain" />
            : <><Camera className="w-10 h-10 text-cgold opacity-40" /><p className="text-cmuted text-sm font-sans text-center">Tap to upload a photo of the wine label</p></>
          }
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelect} className="hidden" />
        </div>
        {image && !result && (
          <button
            onClick={scanLabel}
            disabled={scanning}
            className="w-full py-3 bg-cgold hover:opacity-90 disabled:opacity-50 text-cpaper rounded-xl font-medium font-sans transition flex items-center justify-center gap-2 mb-4"
          >
            {scanning ? <><Loader2 className="w-4 h-4 animate-spin" />Analysing...</> : <><ScanLine className="w-4 h-4" />Analyse Label</>}
          </button>
        )}
        {error && <p className="text-red-400 text-sm mb-4 text-center font-sans">{error}</p>}
        {result && (
          <div className="space-y-4">
            <div className="bg-cbg rounded-xl p-4 border border-cborder">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-cpaper font-semibold font-sans">{result.name}</h3>
                  <p className="text-cgold text-sm font-sans">{result.estate}</p>
                </div>
                <span className="w-3 h-3 rounded-full mt-1 shrink-0" style={{ backgroundColor: wineColorHex(result.color) }} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                <div><p className="text-cmuted text-xs font-sans">Appellation</p><p className="text-cpaper font-sans">{result.appellation}</p></div>
                <div><p className="text-cmuted text-xs font-sans">Vintage</p><p className="text-cpaper font-sans">{result.vintage || "-"}</p></div>
                <div><p className="text-cmuted text-xs font-sans">Country</p><p className="text-cpaper font-sans">{result.country}</p></div>
              </div>
              {result.description && <p className="text-cpaper text-sm leading-relaxed font-sans">{result.description}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-cmuted text-xs mb-1 block font-sans">Quantity</label><input type="number" defaultValue={1} min={1} id="scan-qty" className={inp} /></div>
              <div><label className="text-cmuted text-xs mb-1 block font-sans">Price / btl (EUR)</label><input type="number" defaultValue={0} min={0} step="0.01" id="scan-price" className={inp} /></div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const qty = Number(document.getElementById("scan-qty").value) || 1;
                  const price = Number(document.getElementById("scan-price").value) || 0;
                  onAddWine({ id: Date.now(), quantity: qty, price, ...result, location: "", drinkFrom: result.drinkFrom || "", apogeStart: result.apogeStart || "", apogeEnd: result.apogeEnd || "", drinkBy: result.drinkBy || "" });
                  onClose();
                }}
                className="flex-1 py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans transition flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />Add to Cellar
              </button>
              <button
                onClick={() => { setResult(null); setImage(null); setImageBase64(null); }}
                className="px-4 py-3 bg-cbg border border-cborder text-cpaper rounded-xl font-sans text-sm"
              >
                Rescan
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const WineCellarManager = () => {
  // ── Core state ───────────────────────────────────────────────────────────────
  const [wines, setWines] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("cellar");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedWine, setSelectedWine] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionQuantity, setActionQuantity] = useState("");
  const [actionPrice, setActionPrice] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedWine, setEditedWine] = useState(null);
  const [newWine, setNewWine] = useState({ name: "", appellation: "", vintage: "", estate: "", quantity: "", price: "", color: "Rouge", location: "", drinkFrom: "", apogeStart: "", apogeEnd: "", drinkBy: "" });
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [syncStatus, setSyncStatus] = useState("idle");
  const [syncMsg, setSyncMsg] = useState("");

  // ── Movements / Insights state ─────────────────────────────────────────────
  const [movementStats, setMovementStats] = useState({ yearlyFlow: [], vintageStockByCategory: EMPTY_VINTAGE_STOCK_BY_CATEGORY });
  const [movementsImporting, setMovementsImporting] = useState(false);
  const [movementsStatus, setMovementsStatus] = useState("");

  // ── Recent activity state ────────────────────────────────────────────────
  const [recentMovements, setRecentMovements] = useState([]);
  const [recentMovementsLoading, setRecentMovementsLoading] = useState(false);
  const REASON_OPTIONS = ["conso", "offert", "vendu"];

  // ── Settings ──────────────────────────────────────────────────────────────
  const [showSettings, setShowSettings] = useState(false);
  const [showTargetRanges, setShowTargetRanges] = useState(false);
  const [targetRangeCategory, setTargetRangeCategory] = useState("burgundy_red");
  const [targetRanges, setTargetRanges] = useState(EMPTY_TARGET_RANGES);
  const [targetRangesLoaded, setTargetRangesLoaded] = useState(false);
  const [targetRangesLoading, setTargetRangesLoading] = useState(false);
  const [faceIdRegistering, setFaceIdRegistering] = useState(false);
  const [faceIdStatus, setFaceIdStatus] = useState("");

  // ── Drinking window batch search ───────────────────────────────────────────
  const [windowQueue, setWindowQueue] = useState([]);
  const [windowCurrent, setWindowCurrent] = useState(null);
  const [windowProposal, setWindowProposal] = useState(null);
  const [windowPaused, setWindowPaused] = useState(false);
  const [windowTotal, setWindowTotal] = useState(0);
  const [windowProcessed, setWindowProcessed] = useState(0);
  const [windowSearching, setWindowSearching] = useState(false);
  const [windowEditMode, setWindowEditMode] = useState(false);
  const [windowError, setWindowError] = useState(null);
  const [windowRetryCountdown, setWindowRetryCountdown] = useState(null);
  const abortRef = useRef(null);
  const countdownRef = useRef(null);

  // ── DB load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setSyncStatus("syncing"); setSyncMsg("Loading...");
      try {
        const loaded = await dbGet();
        setWines(loaded);
        setSyncStatus("ok"); setSyncMsg(`${loaded.length} wines loaded`);
      } catch (e) {
        setSyncStatus("error"); setSyncMsg(`Error: ${e.message}`);
      }
      setTimeout(() => setSyncMsg(""), 4000);
    })();
    fetchMovementStats();
  }, []);

  // ── Fetch movement stats ───────────────────────────────────────────────────
  const fetchMovementStats = async () => {
    try {
      const r = await fetch(MOVEMENTS_URL);
      if (!r.ok) return;
      const data = await r.json();
      setMovementStats(data);
    } catch (e) {
      console.error("Failed to fetch movement stats:", e);
    }
  };

  // ── Fetch recent activity ────────────────────────────────────────────────
  const fetchRecentMovements = async () => {
    setRecentMovementsLoading(true);
    try {
      const r = await fetch(`${MOVEMENTS_URL}?list=1&limit=30`);
      if (!r.ok) return;
      const data = await r.json();
      setRecentMovements(data.movements || []);
    } catch (e) {
      console.error("Failed to fetch recent movements:", e);
    } finally {
      setRecentMovementsLoading(false);
    }
  };

  const handleUpdateMovementReason = async (id, reason) => {
    setRecentMovements(prev => prev.map(m => m.id === id ? { ...m, reason } : m));
    try {
      const r = await fetch(MOVEMENTS_URL, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, reason }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (e) {
      console.error("Failed to update movement:", e);
      alert("Failed to update — please try again.");
      fetchRecentMovements();
    }
  };

  const saveWine = useCallback(async (wine) => {
    setSyncStatus("syncing");
    try {
      await dbUpsert(wine);
      setSyncStatus("ok"); setSyncMsg("Saved");
    } catch (e) {
      setSyncStatus("error"); setSyncMsg(`Save failed: ${e.message}`);
    }
    setTimeout(() => setSyncMsg(""), 3000);
  }, []);

  const removeWine = useCallback(async (id) => {
    setSyncStatus("syncing");
    try {
      await dbDelete(id);
      setSyncStatus("ok"); setSyncMsg("Deleted");
    } catch (e) {
      setSyncStatus("error"); setSyncMsg(`Delete failed: ${e.message}`);
    }
    setTimeout(() => setSyncMsg(""), 3000);
  }, []);

  // ── Import ─────────────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus(`Reading ${file.name}...`); setImporting(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        let jsonData;
        if (file.name.toLowerCase().endsWith(".csv")) {
          const lines = evt.target.result.split("\n").filter(l => l.trim());
          const headers = lines[0].split(",").map(h => h.trim().replace(/['"]/g, ""));
          jsonData = lines.slice(1).map(line => {
            const vals = line.split(",").map(v => v.trim().replace(/['"]/g, ""));
            const row = {}; headers.forEach((h, i) => { row[h] = vals[i] || ""; }); return row;
          });
        } else {
          const wb = XLSX.read(new Uint8Array(evt.target.result), { type: "array" });
          jsonData = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        }
        const formatted = jsonData.map((row, idx) => ({
          id: Date.now() + idx, name: row.Name || row.Complement || "", appellation: row.Appellation || row.Region || "",
          vintage: String(row.Vintage || ""), estate: row.Estate || "", quantity: Number(row["Bottle qty"] || row.Quantity || 1),
          price: Number(row["Bottle price"] || row.Price || 0), color: row.Type || row.Color || "Rouge", location: row.Location || "",
          drinkFrom: row["Boire a partir de"] || "", apogeStart: row["Apogee - debut"] || "",
          apogeEnd: row["Apogee - fin"] || "", drinkBy: row["Boire avant"] || "",
          drinkWindowAI: false, grapes: "", country: "", description: "", foodPairing: ""
        })).filter(w => w.name || w.appellation);
        if (!formatted.length) { setImportStatus("No valid rows found."); setImporting(false); return; }
        setWines(prev => [...prev, ...formatted]);
        await Promise.all(formatted.map(dbUpsert));
        await Promise.all(formatted.map(w => recordMovement(w.id, w.vintage, w.quantity, "addition", "achat")));
        fetchMovementStats();
        setImportStatus(`Imported ${formatted.length} wines`);
      } catch (err) { setImportStatus(`Error: ${err.message}`); }
      setImporting(false); setTimeout(() => setImportStatus(""), 6000);
    };
    reader.onerror = () => { setImportStatus("File read error"); setImporting(false); };
    file.name.toLowerCase().endsWith(".csv") ? reader.readAsText(file) : reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  // ── Historical movements import ────────────────────────────────────────────
  const handleMovementsImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMovementsImporting(true);
    setMovementsStatus("Reading file...");
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: "array" });

        const addSheetName = wb.SheetNames.find(n => /addition|entree|entr[ée]e/i.test(n)) || wb.SheetNames[0];
        const addRows = XLSX.utils.sheet_to_json(wb.Sheets[addSheetName]);
        const additions = addRows.map(row => ({
          vintage:  String(row["Vintage"] || row["Millésime"] || row["Millesime"] || ""),
          quantity: Number(row["Quantity"] || row["Quantité"] || row["Quantite"] || 1),
          type:     "addition",
          reason:   "achat",
          date:     parseExcelDate(row["Entry date"] || row["Date d'entrée"] || row["Date entree"] || row["Date"] || ""),
        })).filter(r => r.date && r.vintage);

        const outSheetName = wb.SheetNames.find(n => /withdrawal|sortie/i.test(n)) || wb.SheetNames[1] || wb.SheetNames[0];
        const outRows = XLSX.utils.sheet_to_json(wb.Sheets[outSheetName]);
        const withdrawals = outRows.map(row => ({
          vintage:  String(row["Vintage"] || row["Millésime"] || row["Millesime"] || ""),
          quantity: Number(row["Quantity"] || row["Quantité"] || row["Quantite"] || 1),
          type:     "withdrawal",
          reason:   translateReason(row["Event"] || row["Cause"] || row["Raison"] || "conso"),
          date:     parseExcelDate(row["Exit date"] || row["Date de sortie"] || row["Date sortie"] || row["Date"] || ""),
        })).filter(r => r.date && r.vintage);

        const allRows = [...additions, ...withdrawals];
        setMovementsStatus(`Parsed ${additions.length} additions + ${withdrawals.length} withdrawals. Uploading...`);

        const r = await fetch(MOVEMENTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bulk: true, rows: allRows }),
        });
        const result = await r.json();
        setMovementsStatus(`Done — ${result.inserted} movements imported.`);
        fetchMovementStats();
        setTimeout(() => setMovementsStatus(""), 8000);
      } catch (err) {
        setMovementsStatus(`Error: ${err.message}`);
      } finally {
        setMovementsImporting(false);
      }
    };
    reader.onerror = () => { setMovementsStatus("Failed to read file."); setMovementsImporting(false); };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleAddWine = async (wine) => {
    if (wine.id) {
      setWines(prev => [...prev, wine]);
      await saveWine(wine);
      await recordMovement(wine.id, wine.vintage, wine.quantity, "addition", "achat");
      fetchMovementStats();
      return;
    }
    if (!newWine.name || !newWine.appellation) { alert("Please fill in at least name and appellation"); return; }
    const w = { ...newWine, id: Date.now(), quantity: Number(newWine.quantity) || 1, price: Number(newWine.price) || 0, drinkWindowAI: false, grapes: "", country: "", description: "", foodPairing: "" };
    setWines(prev => [...prev, w]);
    await saveWine(w);
    await recordMovement(w.id, w.vintage, w.quantity, "addition", "achat");
    fetchMovementStats();
    setNewWine({ name: "", appellation: "", vintage: "", estate: "", quantity: "", price: "", color: "Rouge", location: "", drinkFrom: "", apogeStart: "", apogeEnd: "", drinkBy: "" });
    setShowAddForm(false);
  };

  const handleDeleteWine = async (id) => {
    setWines(prev => prev.filter(w => w.id !== id));
    await removeWine(id);
  };

  const handleSetRating = async (wine, rating) => {
    const updated = { ...wine, rating };
    setWines(prev => prev.map(w => w.id === wine.id ? updated : w));
    setSelectedWine(prev => prev && prev.id === wine.id ? updated : prev);
    await saveWine(updated);
  };

  const handleWineAction = async () => {
    const qty = Number(actionQuantity);
    if (!qty || qty <= 0) { alert("Please enter a valid quantity"); return; }
    if (qty > selectedWine.quantity) { alert(`Only ${selectedWine.quantity} bottles available`); return; }
    if (actionType === "sell" && (!actionPrice || Number(actionPrice) <= 0)) { alert("Please enter a valid price"); return; }
    const updated = wines.map(w => {
      if (w.id !== selectedWine.id) return w;
      const nq = w.quantity - qty;
      return nq > 0 ? { ...w, quantity: nq } : null;
    }).filter(Boolean);
    setWines(updated);
    const updatedWine = updated.find(w => w.id === selectedWine.id);
    const reason = actionType === "drink" ? "conso" : actionType === "gift" ? "offert" : "vendu";
    await recordMovement(selectedWine.id, selectedWine.vintage, qty, "withdrawal", reason);
    if (updatedWine) await saveWine(updatedWine);
    else await removeWine(selectedWine.id);
    fetchMovementStats();
    alert(`Successfully recorded ${qty} bottle(s)!`);
    setSelectedWine(null); setActionType(null); setActionQuantity(""); setActionPrice("");
  };

  const handleSaveEdit = async () => {
    if (!editedWine.name || !editedWine.appellation) { alert("Please fill in at least name and appellation"); return; }
    let wineToSave = editedWine;
    if (!windowEditMode) {
      const original = wines.find(w => w.id === editedWine.id);
      const windowChanged = original && (
        original.drinkFrom !== editedWine.drinkFrom ||
        original.apogeStart !== editedWine.apogeStart ||
        original.apogeEnd !== editedWine.apogeEnd ||
        original.drinkBy !== editedWine.drinkBy
      );
      if (windowChanged) wineToSave = { ...editedWine, drinkWindowAI: false };
    }
    setWines(prev => prev.map(w => w.id === wineToSave.id ? wineToSave : w));
    await saveWine(wineToSave);
    setIsEditing(false);
    setEditedWine(null);
    if (windowEditMode) {
      setWindowEditMode(false);
      handleWindowNext();
    } else {
      setSelectedWine(wineToSave);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedWine(null);
    if (windowEditMode) {
      setWindowEditMode(false);
      handleWindowNext();
    }
  };

  // ── Drinking window batch search ───────────────────────────────────────────
  const startRetryCountdown = (wine, seconds = 60) => {
    setWindowRetryCountdown(seconds);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setWindowRetryCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setWindowRetryCountdown(null);
          setWindowError(null);
          fetchWindowProposal(wine);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const fetchWindowProposal = (wine) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setWindowCurrent(wine);
    setWindowSearching(true);
    setWindowProposal(null);
    setWindowError(null);
    fetch(WINDOW_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: wine.name, estate: wine.estate, appellation: wine.appellation, vintage: wine.vintage, color: wine.color }),
      signal: controller.signal,
    })
      .then(async r => {
        if (r.status === 429) { const b = await r.json().catch(() => ({})); const e = new Error(b.error || "Rate limit reached"); e.isRateLimit = true; throw e; }
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `HTTP ${r.status}`); }
        return r.json();
      })
      .then(data => { if (data.error) throw new Error(data.error); setWindowProposal(data); })
      .catch(e => {
        if (e.name === "AbortError") return;
        if (e.isRateLimit) { setWindowError("rate_limit"); startRetryCountdown(wine); }
        else setWindowError(e.message || "Search failed");
      })
      .finally(() => { if (!controller.signal.aborted) setWindowSearching(false); });
  };

  const handleWindowNext = () => {
    setWindowProcessed(p => p + 1);
    setWindowCurrent(null);
    setWindowProposal(null);
    setWindowError(null);
    setWindowQueue(prev => {
      if (prev.length === 0) return prev;
      const [next, ...rest] = prev;
      fetchWindowProposal(next);
      return rest;
    });
  };

  const startWindowSearch = () => {
    const missing = wines.filter(w => !w.drinkFrom || !w.apogeStart || !w.apogeEnd || !w.drinkBy);
    if (!missing.length) { alert("All wines already have complete drinking window data."); return; }
    setWindowTotal(missing.length);
    setWindowProcessed(0);
    setWindowPaused(false);
    setShowSettings(false);
    setWindowQueue(missing.slice(1));
    fetchWindowProposal(missing[0]);
  };

  const handleWindowResume = () => {
    setWindowPaused(false);
    setShowSettings(false);
    setWindowQueue(prev => {
      if (prev.length === 0) return prev;
      const [next, ...rest] = prev;
      fetchWindowProposal(next);
      return rest;
    });
  };

  const handleWindowConfirm = async () => {
    const updated = {
      ...windowCurrent,
      drinkFrom: windowProposal.drinkFrom || windowCurrent.drinkFrom,
      apogeStart: windowProposal.apogeStart || windowCurrent.apogeStart,
      apogeEnd: windowProposal.apogeEnd || windowCurrent.apogeEnd,
      drinkBy: windowProposal.drinkBy || windowCurrent.drinkBy,
      drinkWindowAI: true,
    };
    setWines(prev => prev.map(w => w.id === updated.id ? updated : w));
    await saveWine(updated);
    handleWindowNext();
  };

  const handleWindowDismiss = () => handleWindowNext();

  const handleWindowEdit = () => {
    const prefilled = {
      ...windowCurrent,
      drinkFrom: windowProposal.drinkFrom || windowCurrent.drinkFrom,
      apogeStart: windowProposal.apogeStart || windowCurrent.apogeStart,
      apogeEnd: windowProposal.apogeEnd || windowCurrent.apogeEnd,
      drinkBy: windowProposal.drinkBy || windowCurrent.drinkBy,
      drinkWindowAI: true,
    };
    setWindowEditMode(true);
    setWindowCurrent(null);
    setWindowProposal(null);
    setIsEditing(true);
    setEditedWine(prefilled);
  };

  const handleWindowPause = () => {
    if (abortRef.current) abortRef.current.abort();
    clearInterval(countdownRef.current);
    setWindowRetryCountdown(null);
    setWindowQueue(prev => windowCurrent ? [windowCurrent, ...prev] : prev);
    setWindowPaused(true);
    setWindowCurrent(null);
    setWindowProposal(null);
    setWindowSearching(false);
    setWindowError(null);
  };

  const handleDownloadXLSX = () => {
    const rows = wines.map(w => ({
      Name: w.name, Appellation: w.appellation, Vintage: w.vintage, Estate: w.estate,
      Quantity: w.quantity, "Price (EUR)": w.price, Color: w.color, Location: w.location,
      "Drink From": w.drinkFrom, "Peak Start": w.apogeStart, "Peak End": w.apogeEnd,
      "Drink By": w.drinkBy, "AI Window": w.drinkWindowAI ? "Yes" : "No",
      Grapes: w.grapes, Country: w.country, Description: w.description, "Food Pairing": w.foodPairing,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ma Cave");
    XLSX.writeFile(wb, `ma-cave-${new Date().toISOString().slice(0, 10)}.xlsx`);
    setShowSettings(false);
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const filteredWines = useMemo(() => {
    let result = wines.filter(w =>
      w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.appellation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.vintage.includes(searchTerm) ||
      w.estate.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (activeFilter === "red")   result = result.filter(w => w.color === "Rouge");
    if (activeFilter === "white") result = result.filter(w => w.color === "Blanc");
    if (activeFilter === "rose")  result = result.filter(w => w.color === "Rose");
    if (activeFilter === "ready") {
      const year = new Date().getFullYear();
      result = result.filter(w => {
        if (!w.drinkFrom) return false;
        const from = parseInt(w.drinkFrom);
        const by   = w.drinkBy ? parseInt(w.drinkBy) : Infinity;
        return from <= year && year <= by;
      });
    }
    return result;
  }, [wines, searchTerm, activeFilter]);

  const groupedWines = useMemo(() => {
    const groups = {};
    filteredWines.forEach(w => {
      const key = w.appellation || "Other";
      if (!groups[key]) groups[key] = [];
      groups[key].push(w);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredWines]);

  const stats = useMemo(() => {
    const byAppNuits = {}, byAppBeaune = {}, byAppOther = {}, byVin = {}, byCol = {}, byEstate = {};
    let totalValue = 0, totalBottles = 0;
    wines.forEach(w => {
      const q = w.quantity || 0, p = w.price || 0;
      const cote = classifyCoteAppellation(w.appellation);
      const appGroup = cote === "nuits" ? byAppNuits : cote === "beaune" ? byAppBeaune : byAppOther;
      appGroup[w.appellation] = (appGroup[w.appellation] || 0) + q;
      byVin[w.vintage]     = (byVin[w.vintage]     || 0) + q;
      byCol[w.color]       = (byCol[w.color]        || 0) + q;
      const estate = w.estate && w.estate.trim() ? w.estate.trim() : "Unknown";
      byEstate[estate] = (byEstate[estate] || 0) + q;
      totalValue += q * p; totalBottles += q;
    });
    const toSortedList = obj => Object.entries(obj).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    return {
      byAppellationCoteDeNuits:  toSortedList(byAppNuits),
      byAppellationCoteDeBeaune: toSortedList(byAppBeaune),
      byAppellationOther:        toSortedList(byAppOther),
      byVintage: Object.entries(byVin).map(([name, value]) => ({ name, value })).sort((a, b) => a.name.localeCompare(b.name)),
      byColor:   Object.entries(byCol).map(([name, value]) => ({ name, value })).filter(d => d.value > 0),
      byEstate:  toSortedList(byEstate),
      totalValue, totalBottles,
      avgPrice: totalBottles > 0 ? totalValue / totalBottles : 0,
    };
  }, [wines]);

  // Total bottles in the cellar at the end of each year, worked out backward
  // from the current live total (known exactly) using each year's net flow —
  // there's no stored history of the absolute total, only additions/withdrawals.
  const yearlyFlowWithTotal = useMemo(() => {
    const flow = movementStats.yearlyFlow;
    if (flow.length === 0) return flow;
    const totals = new Array(flow.length);
    totals[flow.length - 1] = stats.totalBottles;
    for (let i = flow.length - 1; i > 0; i--) {
      totals[i - 1] = totals[i] - flow[i].added + flow[i].withdrawn;
    }
    return flow.map((row, i) => ({ ...row, totalBottles: Math.max(0, totals[i]) }));
  }, [movementStats.yearlyFlow, stats.totalBottles]);

  const missingWindowCount = useMemo(() =>
    wines.filter(w => !w.drinkFrom || !w.apogeStart || !w.apogeEnd || !w.drinkBy).length,
  [wines]);

  // Stock per vintage is computed server-side, split into the four chart
  // categories (Burgundy/other × red/white). The "gold" flag (a great
  // vintage worth cellaring long-term) is a manual, user-set judgment call
  // tracked independently per category — 2015 can be gold for Burgundy reds
  // without being gold for other whites.
  const toggleGoldVintage = useCallback(async (category, vintage) => {
    setMovementStats(prev => ({
      ...prev,
      vintageStockByCategory: {
        ...prev.vintageStockByCategory,
        [category]: prev.vintageStockByCategory[category].map(row =>
          row.vintage === vintage ? { ...row, gold: !row.gold } : row
        ),
      },
    }));
    try {
      const r = await fetch(MOVEMENTS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggleGoldVintage", category, vintage }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (e) {
      console.error("Failed to toggle gold vintage:", e);
      fetchMovementStats();
    }
  }, []);

  // ── Target ranges (Settings) ───────────────────────────────────────────────
  const fetchTargetRanges = async () => {
    setTargetRangesLoading(true);
    try {
      const r = await fetch(TARGET_RANGES_URL);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setTargetRanges(data.targetRanges || EMPTY_TARGET_RANGES);
      setTargetRangesLoaded(true);
    } catch (e) {
      console.error("Failed to fetch target ranges:", e);
    }
    setTargetRangesLoading(false);
  };

  const editTargetRangeField = (category, age, field, value) => {
    setTargetRanges(prev => ({
      ...prev,
      [category]: prev[category].map(row => row.age === age ? { ...row, [field]: value } : row),
    }));
  };

  const saveTargetRange = async (category, age) => {
    const row = targetRanges[category]?.find(r => r.age === age);
    if (!row) return;
    const min = Number(row.min), max = Number(row.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) return;
    try {
      const r = await fetch(TARGET_RANGES_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, age, min, max }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      fetchMovementStats();
    } catch (e) {
      console.error("Failed to save target range:", e);
    }
  };

  // ── Face ID / Touch ID ─────────────────────────────────────────────────────
  // A WebAuthn credential is bound to the hostname it was registered under.
  // If the site's domain setup ever changes (e.g. a redirect starts pointing
  // at a different host), a previously-registered credential silently stops
  // matching and biometric login fails with no way to fix it from the lock
  // screen. This re-registers a fresh credential for the CURRENT hostname,
  // overwriting whatever was stored before.
  const registerFaceId = async () => {
    setFaceIdRegistering(true); setFaceIdStatus("");
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          rp: { name: "Wine Cellar", id: window.location.hostname },
          user: { id: crypto.getRandomValues(new Uint8Array(16)), name: "owner", displayName: "Cellar Owner" },
          pubKeyCredParams: [{ alg: -7, type: "public-key" }, { alg: -257, type: "public-key" }],
          authenticatorSelection: { userVerification: "required", residentKey: "preferred" },
          timeout: 60000,
        }
      });
      const r = await fetch(AUTH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save_credential",
          credential: { id: bufferToBase64(credential.rawId), type: credential.type },
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFaceIdStatus(`Registered for ${window.location.hostname}.`);
    } catch (e) {
      setFaceIdStatus("Registration failed: " + e.message);
    }
    setFaceIdRegistering(false);
  };

  const inp = "w-full px-4 py-2.5 bg-cbg text-cpaper rounded-xl border border-cborder focus:border-cgold focus:outline-none font-sans text-sm";
  const filters = [
    { id: "all",   label: "All" },
    { id: "ready", label: "Drink now" },
    { id: "red",   label: "Rouge", dot: "#8B1A1A" },
    { id: "white", label: "Blanc", dot: "#C9A84C" },
    { id: "rose",  label: "Rose",  dot: "#D46070" },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-cbg font-sans" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}>
      <div className="max-w-2xl lg:max-w-5xl mx-auto">

        {/* Header */}
        <div className="px-5 pt-10 pb-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-cgold text-xs uppercase tracking-widest mb-1 font-sans">The Cellar</p>
              <h1 className="text-5xl font-serif text-cpaper leading-none">Ma Cave</h1>
            </div>
            <div className="text-right pt-1">
              <p className="text-4xl font-bold text-cpaper font-sans leading-none">{stats.totalBottles}</p>
              <p className="text-cmuted text-xs font-sans mt-1">bottles &middot; {wines.length} labels</p>
            </div>
          </div>
          {syncMsg && <p className={`text-xs font-sans mt-2 ${syncStatus === "error" ? "text-red-400" : "text-cmuted"}`}>{syncMsg}</p>}
          {importStatus && <div className="mt-3 p-3 bg-cgold/10 border border-cgold/20 rounded-xl text-cpaper text-xs font-sans">{importStatus}</div>}
        </div>

        {/* Search */}
        <div className="px-5 mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cmuted w-4 h-4" />
            <input
              type="text"
              placeholder="Search wines, estates, regions..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-csurf text-cpaper rounded-xl border border-cborder focus:border-cgold focus:outline-none text-base font-sans"
            />
          </div>
        </div>

        {/* Filter chips */}
        <div className="px-5 mb-5 flex gap-2 overflow-x-auto">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-sans transition ${
                activeFilter === f.id ? "bg-cgold text-cpaper font-medium" : "bg-csurf text-cmuted border border-cborder hover:border-cgold"
              }`}
            >
              {f.dot && <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: f.dot }} />}
              {f.label}
            </button>
          ))}
        </div>

        {/* Cellar tab */}
        {activeTab === "cellar" && (
          <div>
            <div className="px-5 mb-2">
              <p className="text-cmuted text-sm font-sans">{filteredWines.length} wines</p>
            </div>
            {filteredWines.length === 0 ? (
              <div className="text-center py-20 px-5">
                <p className="text-cmuted font-sans mb-5">Your cellar is empty.</p>
                <button onClick={() => setShowScanner(true)} className="px-6 py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl text-sm font-medium font-sans flex items-center gap-2 mx-auto">
                  <Camera className="w-4 h-4" />Scan your first label
                </button>
              </div>
            ) : (
              groupedWines.map(([appellation, group]) => (
                <div key={appellation}>
                  <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                    <p className="text-cgold text-xs font-sans uppercase tracking-widest">{appellation}</p>
                    <p className="text-cmuted text-xs font-sans">{group.length}</p>
                  </div>
                  <div className="h-px bg-cborder mx-5 mb-1" />
                  {group.map(wine => (
                    <div
                      key={wine.id}
                      onClick={() => setSelectedWine(wine)}
                      className="flex items-center gap-3 px-5 py-3.5 hover:bg-csurf transition cursor-pointer border-b border-cborder"
                    >
                      <div className="w-0.5 h-10 rounded-full shrink-0" style={{ backgroundColor: wineColorHex(wine.color) }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-cpaper text-sm font-medium font-sans leading-snug truncate">{wine.name}</p>
                        <p className="text-cmuted text-xs font-sans truncate mt-0.5">
                          {wine.estate}{wine.estate && wine.appellation ? " \u00b7 " : ""}{wine.appellation}
                        </p>
                      </div>
                      <div className="text-right shrink-0 mr-1">
                        <p className="text-cpaper text-sm font-bold font-sans">{wine.vintage}</p>
                        <div className="flex items-center justify-end gap-1 mt-0.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: wineColorHex(wine.color) }} />
                          <span className="text-cmuted text-xs font-sans">x{wine.quantity}</span>
                        </div>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteWine(wine.id); }}
                        className="text-cborder hover:text-red-500 transition shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        {/* Insights tab */}
        {activeTab === "insights" && (
          <div className="px-5 space-y-4">

            {/* KPI cards */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Bottles", value: String(stats.totalBottles) },
                { label: "Value",   value: `EUR${stats.totalValue.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}` },
                { label: "Avg / btl", value: `EUR${stats.avgPrice.toFixed(0)}` },
              ].map(({ label, value }) => (
                <div key={label} className="bg-csurf rounded-2xl p-4 border border-cborder">
                  <p className="text-cmuted text-xs uppercase tracking-wider mb-1 font-sans">{label}</p>
                  <p className="text-2xl font-bold text-cpaper font-sans">{value}</p>
                </div>
              ))}
            </div>

            {/* By Appellation */}
            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <h3 className="text-cpaper font-medium mb-4 font-sans">By Appellation</h3>
              {[
                ["Côtes de Nuits",  stats.byAppellationCoteDeNuits],
                ["Côtes de Beaune", stats.byAppellationCoteDeBeaune],
                ["Other",          stats.byAppellationOther],
              ].map(([label, rows]) => rows.length > 0 && (
                <div key={label} className="mb-5 last:mb-0">
                  <p className="text-cgold text-xs uppercase tracking-wider mb-2 font-sans">
                    {label} <span className="text-cmuted normal-case tracking-normal">({rows.length})</span>
                  </p>
                  <div className="divide-y divide-cborder">
                    {rows.map(r => (
                      <div key={r.name} className="flex items-center justify-between gap-3 py-1.5 text-sm font-sans">
                        <span className="text-cpaper">{r.name}</span>
                        <span className="text-cmuted shrink-0">{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* By Domaine */}
            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <h3 className="text-cpaper font-medium mb-4 font-sans">By Domaine</h3>
              <div className="divide-y divide-cborder">
                {stats.byEstate.map(r => (
                  <div key={r.name} className="flex items-center justify-between gap-3 py-1.5 text-sm font-sans">
                    <span className="text-cpaper">{r.name}</span>
                    <span className="text-cmuted shrink-0">{r.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Vintage */}
            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <h3 className="text-cpaper text-base font-semibold mb-4 font-sans">By Vintage</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={stats.byVintage} margin={{ top: 24, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2D5BC" />
                  <XAxis dataKey="name" stroke="#5C4C36" angle={-45} textAnchor="end" height={60} fontSize={12} fontWeight={600} interval={0} />
                  <YAxis stroke="#5C4C36" fontSize={13} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#FFFDF6", border: "1px solid #E2D5BC", borderRadius: "8px", color: "#2A2018", fontSize: 14 }} />
                  <Bar dataKey="value" fill="#8B1A1A" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="value" position="top" fill="#2A2018" fontSize={14} fontWeight={700} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* By Color */}
            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <h3 className="text-cpaper font-medium mb-4 font-sans">By Color</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={stats.byColor} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={90} dataKey="value">
                    {stats.byColor.map((entry, i) => <Cell key={i} fill={wineColorHex(entry.name)} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#FFFDF6", border: "1px solid #E2D5BC", borderRadius: "8px", color: "#2A2018" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Cellar Flow by Year */}
            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <h3 className="text-cpaper font-medium mb-1 font-sans">Cellar Flow by Year</h3>
              <p className="text-cmuted text-xs font-sans mb-4">Bottles added and withdrawn, with total bottles in the cellar over time — since 2020</p>
              {movementStats.yearlyFlow.every(r => r.added === 0 && r.withdrawn === 0) ? (
                <div className="py-8 text-center">
                  <p className="text-cmuted text-sm font-sans mb-3">Import your movements file to see this chart</p>
                  <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer text-sm font-sans border transition
                    ${movementsImporting ? "opacity-50 cursor-not-allowed border-cgold/20 text-cgold/50" : "border-cgold/40 text-cgold hover:bg-cgold/10"}`}>
                    <Upload className="w-4 h-4" />
                    {movementsImporting ? "Importing..." : "Import movements file"}
                    <input type="file" accept=".xlsx,.xls" onChange={handleMovementsImport} className="hidden" disabled={movementsImporting} />
                  </label>
                  {movementsStatus && <p className="text-xs text-cgold/70 font-sans mt-2">{movementsStatus}</p>}
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={yearlyFlowWithTotal} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%" barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2D5BC" vertical={false} />
                      <XAxis dataKey="year" stroke="#83725A" tick={{ fill: "#83725A", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="left" stroke="#83725A" tick={{ fill: "#83725A", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis yAxisId="right" orientation="right" stroke="#83725A" tick={{ fill: "#83725A", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#FFFDF6", border: "1px solid #E2D5BC", borderRadius: "8px", color: "#2A2018" }}
                        formatter={(value, name) => [value, name === "added" ? "Added" : name === "withdrawn" ? "Withdrawn" : "Total bottles"]}
                        labelStyle={{ color: "#C4922A", marginBottom: 4 }}
                      />
                      <Bar yAxisId="left" dataKey="added"     name="added"     fill="#C4922A" radius={[3, 3, 0, 0]} />
                      <Bar yAxisId="left" dataKey="withdrawn" name="withdrawn" fill="#E0574B" radius={[3, 3, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="totalBottles" name="totalBottles" stroke="#2A2018" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 text-xs text-cmuted font-sans">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 rounded-sm bg-[#C4922A]" />Added</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-2 rounded-sm bg-[#E0574B]" />Withdrawn</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-0.5 rounded-sm bg-[#2A2018]" />Total bottles</span>
                    <label className={`ml-auto inline-flex items-center gap-1.5 cursor-pointer transition
                      ${movementsImporting ? "opacity-50 cursor-not-allowed text-cgold/50" : "text-cgold/60 hover:text-cgold"}`}>
                      <Upload className="w-3 h-3" />
                      <span>{movementsImporting ? "Importing..." : "Re-import"}</span>
                      <input type="file" accept=".xlsx,.xls" onChange={handleMovementsImport} className="hidden" disabled={movementsImporting} />
                    </label>
                  </div>
                  {movementsStatus && <p className="text-xs text-cgold/70 font-sans mt-1">{movementsStatus}</p>}
                </>
              )}
            </div>

            {/* Stock by Vintage vs Target, split into four categories */}
            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <h3 className="text-cpaper text-base font-semibold font-sans mb-1">Stock by Vintage vs Target</h3>
              <p className="text-cmuted text-xs font-sans">Bottle outline = current stock. Background shows under / within / over your target range for this vintage's age — set per category under Settings → Target ranges. Click the ★ next to a year to mark it as a gold vintage worth cellaring long-term.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {VINTAGE_CATEGORIES.map(cat => (
                <div key={cat.id} className="bg-csurf rounded-2xl p-4 border border-cborder">
                  <h4 className="text-cpaper text-sm font-semibold font-sans mb-3">{cat.label}</h4>
                  <VintageTargetChart
                    data={movementStats.vintageStockByCategory[cat.id] || []}
                    onToggleGold={vintage => toggleGoldVintage(cat.id, vintage)}
                  />
                </div>
              ))}
            </div>

            <div className="bg-csurf rounded-2xl p-4 border border-cborder">
              <div className="flex items-center gap-5 text-xs text-cmuted font-sans flex-wrap">
                <span className="flex items-center gap-1.5">
                  <span style={{ display:"inline-block", width:12, height:8, background:"rgba(114,47,55,0.4)", borderRadius:2 }} />
                  Under target
                </span>
                <span className="flex items-center gap-1.5">
                  <span style={{ display:"inline-block", width:12, height:8, background:"rgba(74,124,89,0.45)", borderRadius:2 }} />
                  Within target
                </span>
                <span className="flex items-center gap-1.5">
                  <span style={{ display:"inline-block", width:12, height:8, background:"rgba(196,146,42,0.4)", borderRadius:2 }} />
                  Over target
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-cgold text-xs leading-none">★</span>
                  Gold vintage (click a year's star to toggle)
                </span>
              </div>
            </div>

          </div>
        )}

        {/* Activity tab */}
        {activeTab === "activity" && (
          <div className="px-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-cpaper text-lg font-semibold font-sans">Activité récente</h2>
              {recentMovementsLoading && <Loader2 className="w-4 h-4 text-cgold animate-spin" />}
            </div>
            {!recentMovementsLoading && recentMovements.length === 0 && (
              <p className="text-cmuted text-sm font-sans py-8 text-center">Aucun mouvement enregistré pour le moment.</p>
            )}
            <div className="space-y-2">
              {recentMovements.map(m => {
                const reason = translateReason(m.reason);
                const wineId = m.wine_id ?? m.wineId;
                const byId = wines.find(w => w.id === Number(wineId));
                // Movements imported from a historical spreadsheet never had a wine_id;
                // fall back to vintage when it uniquely identifies a wine still in the cellar.
                const vintageMatches = !byId && m.vintage ? wines.filter(w => String(w.vintage) === String(m.vintage)) : [];
                const wine = byId || (vintageMatches.length === 1 ? vintageMatches[0] : null);
                const displayName = wine?.name || m.name || `Millésime ${m.vintage || "?"}`;
                return (
                <div key={m.id} className="bg-csurf rounded-xl p-4 border border-cborder flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-cpaper text-sm font-medium font-sans truncate">
                      {displayName}
                    </p>
                    <p className="text-cmuted text-xs font-sans mt-0.5">
                      {m.date?.slice(0, 10)} - {m.quantity} bouteille{m.quantity === 1 ? "" : "s"} - {m.type === "addition" ? "Ajout" : "Retrait"}
                    </p>
                  </div>
                  {m.type === "withdrawal" ? (
                    <select
                      value={REASON_OPTIONS.includes(reason) ? reason : ""}
                      onChange={e => handleUpdateMovementReason(m.id, e.target.value)}
                      style={{ textAlignLast: "center" }}
                      className="shrink-0 w-28 appearance-none text-center px-3 py-1.5 bg-cbg text-cpaper rounded-lg border border-cborder focus:border-cgold focus:outline-none font-sans text-xs capitalize"
                    >
                      {!REASON_OPTIONS.includes(reason) && <option value="" disabled>{reason || "inconnu"}</option>}
                      {REASON_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className="shrink-0 w-28 flex items-center justify-center px-3 py-1.5 bg-cgold/10 text-cgold rounded-lg border border-cgold/30 font-sans text-xs capitalize">
                      {reason || "achat"}
                    </span>
                  )}
                </div>
              );})}
            </div>
          </div>
        )}
      </div>

      {/* Bottom tab bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-csurf border-t border-cborder z-40" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="max-w-2xl mx-auto flex items-center justify-around px-6 py-3">
          <button onClick={() => setActiveTab("cellar")} className={`flex flex-col items-center gap-1 ${activeTab === "cellar" ? "text-cgold" : "text-cmuted"}`}>
            <Home className="w-5 h-5" /><span className="text-xs font-sans">Cellar</span>
          </button>
          <button
            onClick={() => { setActiveTab("activity"); fetchRecentMovements(); }}
            className={`flex flex-col items-center gap-1 ${activeTab === "activity" ? "text-cgold" : "text-cmuted"}`}
          >
            <Clock className="w-5 h-5" />
            <span className="text-xs font-sans">Activity</span>
          </button>
          <button
            onClick={() => setShowAddMenu(prev => !prev)}
            className="w-12 h-12 bg-cburg rounded-2xl flex items-center justify-center hover:opacity-90 transition -mt-4 shadow-lg"
          >
            <Plus className={`w-6 h-6 text-[#F1E9DA] transition-transform duration-200 ${showAddMenu ? "rotate-45" : ""}`} />
          </button>
          <button
            onClick={() => { setActiveTab("insights"); fetchMovementStats(); }}
            className={`flex flex-col items-center gap-1 ${activeTab === "insights" ? "text-cgold" : "text-cmuted"}`}
          >
            <BarChart3 className="w-5 h-5" /><span className="text-xs font-sans">Insights</span>
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className={`flex flex-col items-center gap-1 relative ${showSettings ? "text-cgold" : "text-cmuted"} hover:text-cgold transition`}
          >
            <Settings className="w-5 h-5" />
            {windowPaused && <span className="absolute -top-1 -right-2 w-2 h-2 bg-cgold rounded-full" />}
            <span className="text-xs font-sans">Settings</span>
          </button>
        </div>
      </div>

      {showScanner && <LabelScanner onClose={() => setShowScanner(false)} onAddWine={handleAddWine} />}

      {/* FAB speed-dial */}
      {showAddMenu && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowAddMenu(false)} />
          <div className="fixed left-0 right-0 flex flex-col items-center gap-2.5 z-50" style={{ bottom: "calc(5rem + env(safe-area-inset-bottom))" }}>
            <label className="flex items-center gap-3 px-5 py-3 bg-csurf border border-cborder rounded-2xl cursor-pointer hover:border-cgold transition shadow-xl">
              <Upload className="w-4 h-4 text-cgold shrink-0" />
              <span className="text-cpaper text-sm font-sans">Import a wine list</span>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { handleFileUpload(e); setShowAddMenu(false); }} className="hidden" disabled={importing} />
            </label>
            <button onClick={() => { setShowAddMenu(false); setShowAddForm(true); }} className="flex items-center gap-3 px-5 py-3 bg-csurf border border-cborder rounded-2xl hover:border-cgold transition shadow-xl">
              <Plus className="w-4 h-4 text-cgold shrink-0" />
              <span className="text-cpaper text-sm font-sans">Input a wine manually</span>
            </button>
            <button onClick={() => { setShowAddMenu(false); setShowScanner(true); }} className="flex items-center gap-3 px-5 py-3 bg-csurf border border-cborder rounded-2xl hover:border-cgold transition shadow-xl">
              <Camera className="w-4 h-4 text-cgold shrink-0" />
              <span className="text-cpaper text-sm font-sans">Scan a label</span>
            </button>
          </div>
        </>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/85 flex items-end justify-center z-50">
          <div className="bg-csurf rounded-t-2xl p-6 w-full sm:max-w-lg border border-cborder max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-cpaper font-sans">Settings</h2>
              <button onClick={() => setShowSettings(false)} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-cgold text-xs uppercase tracking-wider mb-3 font-sans">Import</p>
            <label className="flex items-center gap-3 p-4 bg-cbg rounded-xl border border-cborder cursor-pointer hover:border-cgold transition mb-6">
              <Upload className="w-5 h-5 text-cmuted shrink-0" />
              <div className="flex-1">
                <p className="text-cpaper text-sm font-sans">Import Excel / CSV</p>
                <p className="text-cmuted text-xs font-sans">Upload your wine inventory file</p>
              </div>
              {importing ? <Loader2 className="w-4 h-4 text-cgold animate-spin shrink-0" /> : <span className="text-cmuted text-xs font-sans shrink-0">Choose file</span>}
              <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { handleFileUpload(e); setShowSettings(false); }} className="hidden" disabled={importing} />
            </label>
            <p className="text-cgold text-xs uppercase tracking-wider mb-3 font-sans">Export</p>
            <button onClick={handleDownloadXLSX} className="flex items-center gap-3 w-full p-4 bg-cbg rounded-xl border border-cborder hover:border-cgold transition mb-6">
              <Download className="w-5 h-5 text-cmuted shrink-0" />
              <div className="text-left">
                <p className="text-cpaper text-sm font-sans">Download as Excel</p>
                <p className="text-cmuted text-xs font-sans">{wines.length} wine{wines.length !== 1 ? "s" : ""} · .xlsx format</p>
              </div>
            </button>
            <p className="text-cgold text-xs uppercase tracking-wider mb-3 font-sans">Drinking Window</p>
            {windowPaused ? (
              <div className="p-4 bg-cbg rounded-xl border border-cgold/30">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full bg-cgold" />
                  <p className="text-cpaper text-sm font-sans font-medium">Search paused</p>
                </div>
                <p className="text-cmuted text-xs font-sans mb-4">{windowQueue.length} wine{windowQueue.length !== 1 ? "s" : ""} remaining · {windowProcessed} processed</p>
                <button onClick={handleWindowResume} className="w-full py-2.5 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans text-sm">Resume search</button>
              </div>
            ) : (
              <button onClick={startWindowSearch} disabled={missingWindowCount === 0} className="flex items-center gap-3 w-full p-4 bg-cbg rounded-xl border border-cborder hover:border-cgold transition disabled:opacity-40 disabled:cursor-not-allowed">
                <Globe className="w-5 h-5 text-cmuted shrink-0" />
                <div className="text-left">
                  <p className="text-cpaper text-sm font-sans">Search drinking windows</p>
                  <p className="text-cmuted text-xs font-sans">
                    {missingWindowCount === 0 ? "All wines have complete data" : `${missingWindowCount} wine${missingWindowCount !== 1 ? "s" : ""} missing data`}
                  </p>
                </div>
              </button>
            )}
            <p className="text-cgold text-xs uppercase tracking-wider mb-3 mt-6 font-sans">Cellar targets</p>
            <button
              onClick={() => { setShowTargetRanges(true); if (!targetRangesLoaded) fetchTargetRanges(); }}
              className="flex items-center gap-3 w-full p-4 bg-cbg rounded-xl border border-cborder hover:border-cgold transition"
            >
              <BarChart3 className="w-5 h-5 text-cmuted shrink-0" />
              <div className="text-left">
                <p className="text-cpaper text-sm font-sans">Target ranges</p>
                <p className="text-cmuted text-xs font-sans">Set the bottle range per year of age, for each category</p>
              </div>
            </button>
            {window.PublicKeyCredential && (
              <>
                <p className="text-cgold text-xs uppercase tracking-wider mb-3 mt-6 font-sans">Security</p>
                <button
                  onClick={registerFaceId}
                  disabled={faceIdRegistering}
                  className="flex items-center gap-3 w-full p-4 bg-cbg rounded-xl border border-cborder hover:border-cgold transition disabled:opacity-50"
                >
                  <Fingerprint className="w-5 h-5 text-cmuted shrink-0" />
                  <div className="text-left">
                    <p className="text-cpaper text-sm font-sans">{faceIdRegistering ? "Registering…" : "Register Face ID / Touch ID"}</p>
                    <p className="text-cmuted text-xs font-sans">Run this again any time biometric sign-in stops working, e.g. after the site's domain changes</p>
                  </div>
                </button>
                {faceIdStatus && <p className="text-cgold text-xs font-sans mt-2">{faceIdStatus}</p>}
              </>
            )}
          </div>
        </div>
      )}

      {/* Target ranges editor */}
      {showTargetRanges && (
        <div className="fixed inset-0 bg-black/85 flex items-end justify-center z-50">
          <div className="bg-csurf rounded-t-2xl p-6 w-full sm:max-w-lg border border-cborder max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-cpaper font-sans">Target ranges</h2>
              <button onClick={() => setShowTargetRanges(false)} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-1.5 flex-wrap mb-4">
              {VINTAGE_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setTargetRangeCategory(cat.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-sans transition ${
                    targetRangeCategory === cat.id ? "bg-cgold text-cpaper font-medium" : "bg-cbg text-cmuted border border-cborder hover:border-cgold"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            {targetRangesLoading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-cgold animate-spin" /></div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-2 mb-1 text-cmuted text-xs font-sans">
                  <span className="flex-1">Age of vintage</span>
                  <span className="w-16 text-center">Min</span>
                  <span className="w-16 text-center">Max</span>
                </div>
                <div className="space-y-1.5">
                  {(targetRanges[targetRangeCategory] || []).map(row => (
                    <div key={row.age} className="flex items-center gap-2 bg-cbg rounded-lg border border-cborder px-2 py-1.5">
                      <span className="flex-1 text-cpaper text-sm font-sans">{row.age} year{row.age !== 1 ? "s" : ""} ({new Date().getFullYear() - row.age})</span>
                      <input
                        type="number" min="0" value={row.min}
                        onChange={e => editTargetRangeField(targetRangeCategory, row.age, "min", e.target.value)}
                        onBlur={() => saveTargetRange(targetRangeCategory, row.age)}
                        className="w-16 px-1.5 py-1 bg-csurf text-cpaper text-sm text-center rounded-md border border-cborder focus:border-cgold focus:outline-none font-sans"
                      />
                      <input
                        type="number" min="0" value={row.max}
                        onChange={e => editTargetRangeField(targetRangeCategory, row.age, "max", e.target.value)}
                        onBlur={() => saveTargetRange(targetRangeCategory, row.age)}
                        className="w-16 px-1.5 py-1 bg-csurf text-cpaper text-sm text-center rounded-md border border-cborder focus:border-cgold focus:outline-none font-sans"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Drinking window proposal */}
      {(windowCurrent || windowSearching) && !windowPaused && !isEditing && (
        <div className="fixed inset-0 bg-black/85 flex items-end justify-center z-50">
          <div className="bg-csurf rounded-t-2xl p-6 w-full sm:max-w-lg border border-cborder">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-cgold text-xs uppercase tracking-wider font-sans mb-0.5">Drinking Window Search</p>
                <p className="text-cmuted text-xs font-sans">{windowProcessed + 1} of {windowTotal}</p>
              </div>
              <button onClick={handleWindowPause} className="flex items-center gap-1.5 px-3 py-1.5 bg-cbg border border-cborder hover:border-cgold text-cmuted hover:text-cpaper rounded-lg text-xs font-sans transition">Pause</button>
            </div>
            <div className="h-0.5 bg-cborder rounded-full mb-4 overflow-hidden">
              <div className="h-full bg-cgold rounded-full transition-all" style={{ width: `${(windowProcessed / windowTotal) * 100}%` }} />
            </div>
            {windowCurrent && (
              <div className="bg-cbg rounded-xl p-3 border border-cborder mb-4">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-1 h-4 rounded-full shrink-0" style={{ backgroundColor: wineColorHex(windowCurrent.color) }} />
                  <p className="text-cpaper text-sm font-medium font-sans leading-snug">{windowCurrent.name}</p>
                </div>
                <p className="text-cmuted text-xs font-sans ml-3">{windowCurrent.estate}{windowCurrent.estate && windowCurrent.vintage ? " · " : ""}{windowCurrent.vintage}{windowCurrent.appellation ? ` · ${windowCurrent.appellation}` : ""}</p>
              </div>
            )}
            {windowSearching && (
              <div className="flex items-center justify-center gap-2 py-8">
                <Loader2 className="w-5 h-5 text-cgold animate-spin" />
                <p className="text-cmuted text-sm font-sans">Searching wine databases…</p>
              </div>
            )}
            {windowError && !windowSearching && (
              <div className="py-4">
                {windowError === "rate_limit" ? (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full border-2 border-cgold/30 flex items-center justify-center shrink-0">
                        <span className="text-cgold text-xs font-bold font-sans">{windowRetryCountdown ?? "…"}</span>
                      </div>
                      <div>
                        <p className="text-cpaper text-sm font-sans font-medium">Rate limit reached</p>
                        <p className="text-cmuted text-xs font-sans">Auto-retrying in {windowRetryCountdown ?? "…"}s</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button onClick={() => { clearInterval(countdownRef.current); setWindowRetryCountdown(null); setWindowError(null); fetchWindowProposal(windowCurrent); }} className="py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans text-sm">Retry now</button>
                      <button onClick={() => { clearInterval(countdownRef.current); setWindowRetryCountdown(null); handleWindowDismiss(); }} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Skip</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-red-400 text-sm font-sans text-center mb-4">{windowError}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => { setWindowError(null); fetchWindowProposal(windowCurrent); }} className="py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans text-sm">Retry</button>
                      <button onClick={handleWindowDismiss} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Skip</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {windowProposal && !windowSearching && !windowError && (
              <>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[["Drink from", windowCurrent.drinkFrom, windowProposal.drinkFrom], ["Peak start", windowCurrent.apogeStart, windowProposal.apogeStart], ["Peak end", windowCurrent.apogeEnd, windowProposal.apogeEnd], ["Drink by", windowCurrent.drinkBy, windowProposal.drinkBy]].map(([label, current, proposed]) => (
                    <div key={label} className="bg-cgold/5 border border-cgold/20 rounded-xl p-3">
                      <p className="text-cmuted text-xs font-sans mb-1">{label}</p>
                      {current && current !== proposed && <p className="text-cmuted text-xs font-sans line-through leading-tight">{current}</p>}
                      <p className="text-cgold text-sm font-sans font-medium">{proposed || "—"}</p>
                    </div>
                  ))}
                </div>
                {windowProposal.source && <p className="text-cmuted text-xs font-sans mb-4 text-center">Source: {windowProposal.source}</p>}
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={handleWindowConfirm} className="py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans text-sm">Confirm</button>
                  <button onClick={handleWindowEdit} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Edit</button>
                  <button onClick={handleWindowDismiss} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Dismiss</button>
                  <button onClick={handleWindowPause} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Pause</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Wine detail modal */}
      {selectedWine && !actionType && !isEditing && (
        <div className="fixed inset-0 bg-black/85 flex items-end sm:items-center justify-center z-50">
          <div className="bg-csurf rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-lg border border-cborder max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-5">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-4 rounded-full" style={{ backgroundColor: wineColorHex(selectedWine.color) }} />
                  <p className="text-cmuted text-xs font-sans uppercase tracking-wide">{selectedWine.color}</p>
                </div>
                <h2 className="text-xl font-semibold text-cpaper font-sans leading-snug">{selectedWine.name}</h2>
                <p className="text-cgold text-sm font-sans mt-0.5">{selectedWine.estate}</p>
              </div>
              <button onClick={() => setSelectedWine(null)} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[["Appellation", selectedWine.appellation], ["Vintage", selectedWine.vintage], ["Quantity", `${selectedWine.quantity} bottles`], ["Price / btl", `EUR${Number(selectedWine.price).toFixed(2)}`], ["Location", selectedWine.location || "-"], ["Country", selectedWine.country || "-"]].map(([label, val]) => (
                <div key={label} className="bg-cbg rounded-xl p-3 border border-cborder">
                  <p className="text-cmuted text-xs font-sans mb-0.5">{label}</p>
                  <p className="text-cpaper font-medium font-sans text-sm">{val}</p>
                </div>
              ))}
            </div>
            <div className="bg-cbg border border-cborder rounded-xl p-4 mb-4 flex items-center justify-between">
              <p className="text-cmuted text-xs uppercase tracking-wider font-sans">My Rating</p>
              <StarRating value={selectedWine.rating} onChange={r => handleSetRating(selectedWine, r)} />
            </div>
            {(selectedWine.description || selectedWine.grapes) && (
              <div className="bg-cgold/5 border border-cgold/15 rounded-xl p-4 mb-4">
                <p className="text-cgold text-xs uppercase tracking-wider mb-2 font-sans">AI Analysis</p>
                {selectedWine.grapes && <p className="text-cmuted text-sm mb-1 font-sans">Grapes: <span className="text-cpaper">{selectedWine.grapes}</span></p>}
                {selectedWine.description && <p className="text-cpaper text-sm leading-relaxed font-sans">{selectedWine.description}</p>}
                {selectedWine.foodPairing && <p className="text-cmuted text-sm mt-1 font-sans">Pairing: {selectedWine.foodPairing}</p>}
              </div>
            )}
            {(selectedWine.drinkFrom || selectedWine.apogeStart || selectedWine.drinkBy) && (
              <div className={`rounded-xl p-4 border mb-5 ${selectedWine.drinkWindowAI ? "bg-cgold/5 border-cgold/25" : "bg-cbg border-cborder"}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-cmuted text-xs uppercase tracking-wider font-sans">Drinking Window</p>
                  {selectedWine.drinkWindowAI && <span className="flex items-center gap-1 text-cgold text-xs font-sans"><span aria-hidden>✦</span> AI</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[["Drink from", selectedWine.drinkFrom], ["Peak start", selectedWine.apogeStart], ["Peak end", selectedWine.apogeEnd], ["Drink by", selectedWine.drinkBy]].map(([label, val]) =>
                    val ? (
                      <div key={label}>
                        <p className="text-cmuted text-xs font-sans">{label}</p>
                        <p className={`text-sm font-sans ${selectedWine.drinkWindowAI ? "text-cgold" : "text-cpaper"}`}>{val}</p>
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setActionType("drink")} className="py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans text-sm">Drink</button>
              <button onClick={() => setActionType("sell")} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Sell</button>
              <button onClick={() => setActionType("gift")} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Gift</button>
              <button onClick={() => { setIsEditing(true); setEditedWine({ ...selectedWine }); }} className="py-3 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans text-sm">Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {isEditing && editedWine && (
        <div className="fixed inset-0 bg-black/85 flex items-end sm:items-center justify-center z-50">
          <div className="bg-csurf rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-lg border border-cborder max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-cpaper font-sans">Edit Wine</h2>
              <button onClick={handleCancelEdit} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[["Name *", "name", "text"], ["Appellation *", "appellation", "text"], ["Vintage", "vintage", "text"], ["Estate", "estate", "text"], ["Quantity", "quantity", "number"], ["Price (EUR)", "price", "number"]].map(([label, field, type]) => (
                  <div key={field}>
                    <label className="block text-cmuted text-xs mb-1 font-sans">{label}</label>
                    <input type={type} value={editedWine[field]} onChange={e => setEditedWine({ ...editedWine, [field]: type === "number" ? Number(e.target.value) : e.target.value })} className={inp} />
                  </div>
                ))}
                <div>
                  <label className="block text-cmuted text-xs mb-1 font-sans">Type</label>
                  <select value={editedWine.color} onChange={e => setEditedWine({ ...editedWine, color: e.target.value })} className={inp}>
                    {["Rouge", "Blanc", "Rose", "Sparkling"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-cmuted text-xs mb-1 font-sans">Location</label>
                  <input type="text" value={editedWine.location} onChange={e => setEditedWine({ ...editedWine, location: e.target.value })} className={inp} />
                </div>
              </div>
              <div className="border-t border-cborder pt-3">
                <p className="text-cmuted text-xs uppercase tracking-wider mb-3 font-sans">Drinking Window</p>
                <div className="grid grid-cols-2 gap-3">
                  {[["Drink from", "drinkFrom"], ["Peak start", "apogeStart"], ["Peak end", "apogeEnd"], ["Drink by", "drinkBy"]].map(([label, field]) => (
                    <div key={field}>
                      <label className="block text-cmuted text-xs mb-1 font-sans">{label}</label>
                      <input type="text" value={editedWine[field]} onChange={e => setEditedWine({ ...editedWine, [field]: e.target.value })} className={inp} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleSaveEdit} className="flex-1 py-2.5 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans">Save</button>
                <button onClick={handleCancelEdit} className="px-5 py-2.5 bg-cbg border border-cborder text-cpaper rounded-xl font-sans text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drink / Sell / Gift modal */}
      {selectedWine && actionType && (
        <div className="fixed inset-0 bg-black/85 flex items-end sm:items-center justify-center z-50">
          <div className="bg-csurf rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-md border border-cborder">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-cpaper font-sans">{actionType === "drink" ? "Drink" : actionType === "gift" ? "Gift" : "Sell"}</h2>
              <button onClick={() => { setActionType(null); setActionQuantity(""); setActionPrice(""); }} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4 p-4 bg-cbg rounded-xl border border-cborder">
              <p className="text-cpaper font-medium font-sans">{selectedWine.name}</p>
              <p className="text-cmuted text-sm font-sans">{selectedWine.appellation} - {selectedWine.vintage}</p>
              <p className="text-cmuted text-sm font-sans mt-1">{selectedWine.quantity} bottles available</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-cmuted text-xs mb-2 font-sans">Number of bottles</label>
                <input type="number" min="1" max={selectedWine.quantity} value={actionQuantity} onChange={e => setActionQuantity(e.target.value)} className={inp} />
              </div>
              {actionType === "sell" && (
                <div>
                  <label className="block text-cmuted text-xs mb-2 font-sans">Price per bottle (EUR)</label>
                  <input type="number" min="0" step="0.01" value={actionPrice} onChange={e => setActionPrice(e.target.value)} className={inp} />
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={handleWineAction} className="flex-1 py-2.5 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans">Confirm</button>
                <button onClick={() => { setActionType(null); setActionQuantity(""); setActionPrice(""); }} className="px-5 py-2.5 bg-cbg border border-cborder text-cpaper rounded-xl font-sans text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add wine modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/85 flex items-end sm:items-center justify-center z-50">
          <div className="bg-csurf rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-md border border-cborder max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-1">
              <h2 className="text-lg font-semibold text-cpaper font-sans">New bottle</h2>
              <button onClick={() => setShowAddForm(false)} className="text-cmuted hover:text-cpaper transition"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-cmuted text-sm font-sans mb-5">Or use the camera icon to scan a label.</p>
            <div className="space-y-3">
              {[["Wine name *", "name"], ["Estate / producer", "estate"], ["Appellation *", "appellation"]].map(([ph, field]) => (
                <input key={field} type="text" placeholder={ph} value={newWine[field]} onChange={e => setNewWine({ ...newWine, [field]: e.target.value })} className={inp} />
              ))}
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="Vintage" value={newWine.vintage} onChange={e => setNewWine({ ...newWine, vintage: e.target.value })} className={inp} />
                <select value={newWine.color} onChange={e => setNewWine({ ...newWine, color: e.target.value })} className={inp}>
                  {["Rouge", "Blanc", "Rose", "Sparkling"].map(c => <option key={c}>{c}</option>)}
                </select>
                <input type="number" placeholder="Quantity" value={newWine.quantity} onChange={e => setNewWine({ ...newWine, quantity: e.target.value })} className={inp} />
                <input type="number" placeholder="Price / btl (EUR)" value={newWine.price} onChange={e => setNewWine({ ...newWine, price: e.target.value })} className={inp} />
              </div>
              <div className="border-t border-cborder pt-3">
                <p className="text-cmuted text-xs uppercase tracking-wider mb-3 font-sans">Drinking Window</p>
                <div className="grid grid-cols-2 gap-3">
                  {[["Drink from", "drinkFrom"], ["Peak start", "apogeStart"], ["Peak end", "apogeEnd"], ["Drink by", "drinkBy"]].map(([ph, field]) => (
                    <input key={field} type="text" placeholder={ph} value={newWine[field]} onChange={e => setNewWine({ ...newWine, [field]: e.target.value })} className={inp} />
                  ))}
                </div>
              </div>
              <button onClick={() => handleAddWine({})} className="w-full py-3 bg-cgold hover:opacity-90 text-cpaper rounded-xl font-medium font-sans">Add to Cellar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function App() {
  const [token, setToken] = useState(null);
  if (!token) return <Auth onAuthenticated={setToken} />;
  return <WineCellarManager />;
}
